import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Star, Send, Camera } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Stars({ rating, size = "w-4 h-4", interactive = false, onRate }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`star-${i}`}
          className={`${size} transition-colors ${interactive ? "cursor-pointer" : ""} ${
            i < rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#D4AF37]/20"
          }`}
          onClick={interactive ? () => onRate(i + 1) : undefined}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="glass-card rounded-xl border border-[#D4AF37]/15 overflow-hidden"
      data-testid={`review-card-${index}`}
    >
      {review.photo_url && (
        <div className="relative h-56 overflow-hidden">
          <img
            src={review.photo_url.startsWith("http") ? review.photo_url : `${BACKEND_URL}${review.photo_url}`}
            alt={`${review.name}'s reveal`}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        </div>
      )}
      <div className="p-6">
        <Stars rating={review.rating} />
        <p className="font-montserrat text-white/70 text-sm leading-relaxed mt-4 mb-5 italic">
          "{review.text}"
        </p>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
            <span className="font-playfair text-[#D4AF37] text-xs font-bold">{review.name.charAt(0)}</span>
          </div>
          <p className="font-playfair text-white text-sm font-semibold">{review.name}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function CustomerReviews() {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "", photo_url: "" });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/reviews`).then(({ data }) => {
      if (!cancelled) setReviews(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Photo is ${(file.size / 1024 / 1024).toFixed(1)}MB — max 25MB.`);
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/reviews/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60 * 1000,
      });
      setForm((p) => ({ ...p, photo_url: data.url }));
      toast.success("Photo attached");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Try again";
      toast.error(`Photo upload failed: ${detail}`);
    }
    setUploadingPhoto(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please enter your name and review");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      setForm({ name: "", rating: 5, text: "", photo_url: "" });
      setShowForm(false);
      toast.success("Thank you! Your review will appear once approved by Vicki Ann.");
    } catch {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  const labelClass = "text-[#D4AF37]/80 text-xs tracking-[0.2em] uppercase font-montserrat mb-2";
  const inputClass = "bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 font-montserrat h-12";

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 section-glow-pink" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">
            Real Reveals, Real Joy
          </p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">
            Customer Reviews
          </h2>
          <div className="gold-line w-24 mx-auto mt-6" />

          {reviews.length > 0 && (
            <div className="flex items-center justify-center gap-3 mt-8" data-testid="reviews-summary">
              <Stars rating={Math.round(Number(avgRating))} size="w-5 h-5" />
              <span className="font-playfair text-white text-xl font-bold">{avgRating}</span>
              <span className="font-montserrat text-white/40 text-sm">({reviews.length} review{reviews.length !== 1 ? "s" : ""})</span>
            </div>
          )}
        </motion.div>

        {/* Review Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {reviews.map((review, i) => (
            <ReviewCard key={review.id || i} review={review} index={i} />
          ))}
        </div>

        {/* Write a Review */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              data-testid="write-review-btn"
              className="btn-gold px-8 py-3 rounded-full text-sm font-montserrat inline-flex items-center gap-2"
            >
              <Star className="w-4 h-4" />
              Write a Review
            </button>
          ) : (
            <div className="max-w-lg mx-auto glass-card rounded-xl border border-[#D4AF37]/20 p-8" data-testid="review-form">
              <h3 className="font-playfair text-white text-xl mb-6">Share Your Experience</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className={labelClass}>Your Name *</Label>
                  <Input
                    data-testid="review-name-input"
                    className={inputClass}
                    placeholder="Enter your name"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Rating *</Label>
                  <div className="mt-1">
                    <Stars rating={form.rating} size="w-7 h-7" interactive onRate={(r) => setForm((p) => ({ ...p, rating: r }))} />
                  </div>
                </div>
                <div>
                  <Label className={labelClass}>Your Review *</Label>
                  <Textarea
                    data-testid="review-text-input"
                    className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 font-montserrat min-h-[100px]"
                    placeholder="Tell us about your mystery scoop experience..."
                    value={form.text}
                    onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className={labelClass}>Reveal Photo (optional)</Label>
                  {form.photo_url ? (
                    <div className="relative rounded-lg overflow-hidden border border-[#D4AF37]/20" data-testid="review-photo-preview">
                      <img
                        src={form.photo_url.startsWith("http") ? form.photo_url : `${BACKEND_URL}${form.photo_url}`}
                        alt="Your reveal"
                        className="w-full h-40 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, photo_url: "" }))}
                        className="absolute top-2 right-2 px-3 py-1 rounded-full bg-black/70 text-white/80 hover:text-white text-xs font-montserrat"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-[#D4AF37]/20 rounded-lg cursor-pointer hover:border-[#D4AF37]/40 transition-colors" data-testid="review-photo-upload">
                      <Camera className="w-6 h-6 text-[#D4AF37]/40 mb-2" />
                      <span className="font-montserrat text-white/40 text-xs">
                        {uploadingPhoto ? "Uploading..." : "Tap to upload a photo"}
                      </span>
                      <span className="font-montserrat text-white/20 text-[10px] mt-1">JPG, PNG, HEIC (max 25MB)</span>
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} className="hidden" />
                    </label>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-review-btn"
                    className="btn-gold flex-1 py-3 rounded-full text-sm font-montserrat flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? "Submitting..." : "Submit Review"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    data-testid="cancel-review-btn"
                    className="px-6 py-3 rounded-full text-sm font-montserrat text-white/40 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
